import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Calendar, Ban, Trash2, Mail, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = {
  inperson_1x_monthly: "1×/wk",
  inperson_2x_monthly: "2×/wk",
  inperson_3x_monthly: "3×/wk",
  inperson_3mo_2x: "3-mo pkg",
  inperson_6mo_2x: "6-mo pkg",
  inperson_single: "Single",
};

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function CoachSessionsPanel() {
  const [upcoming, setUpcoming] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b] = await Promise.all([
        api.get("/sessions/admin/upcoming").catch(() => ({ data: [] })),
        api.get("/sessions/blocks").catch(() => ({ data: [] })),
      ]);
      setUpcoming(u.data || []);
      setBlocks(b.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // "This week" = anything before end-of-day 7 days from now.
  const thisWeek = useMemo(() => {
    const cutoff = new Date(Date.now() + 7 * 86400000).toISOString();
    return upcoming.filter((s) => s.scheduled_at <= cutoff);
  }, [upcoming]);

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockStart || !blockEnd) return toast.error("Pick start and end times");
    setSaving(true);
    try {
      await api.post("/sessions/blocks", {
        start_at: new Date(blockStart).toISOString(),
        end_at: new Date(blockEnd).toISOString(),
        reason: blockReason || null,
      });
      toast.success("Time blocked");
      setBlockStart(""); setBlockEnd(""); setBlockReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not block");
    } finally {
      setSaving(false);
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm("Un-block this time slot?")) return;
    try {
      await api.delete(`/sessions/blocks/${id}`);
      toast.success("Slot un-blocked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not un-block");
    }
  };

  return (
    <div className="obs-card" data-testid="coach-sessions-panel">
      <div className="flex items-center gap-3 mb-3">
        <Calendar className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">Coach schedule</div>
          <h3 className="font-heading text-2xl uppercase">Upcoming sessions · 7d</h3>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          {thisWeek.length} this week
        </span>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : thisWeek.length === 0 ? (
        <p className="text-xs text-zinc-500">No sessions on the books for the next 7 days.</p>
      ) : (
        <div className="space-y-2">
          {thisWeek.map((s) => (
            <div
              key={s.id}
              data-testid={`coach-session-${s.id}`}
              className="flex items-center gap-3 p-2 border border-obsidian-600 rounded-sm"
            >
              <div className="w-32 flex-shrink-0">
                <div className="text-sm text-zinc-100">{fmt(s.scheduled_at)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{s.client_name || "—"}</div>
                {s.note && <div className="text-[11px] text-zinc-500 truncate">{s.note}</div>}
              </div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-obsidian-600 rounded-full text-zinc-400">
                {PLAN_LABEL[s.plan] || s.plan}
              </span>
              {s.client_email && (
                <a
                  href={`mailto:${s.client_email}?subject=Your Ripple Effect session on ${new Date(s.scheduled_at).toLocaleDateString()}`}
                  className="ghost-btn px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                  title="Reschedule via email"
                >
                  <Mail className="h-3 w-3" /> Contact
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Block time slot form */}
      <div className="mt-6 pt-4 border-t border-obsidian-600">
        <div className="flex items-center gap-3 mb-3">
          <Ban className="h-4 w-4 text-blaze" />
          <div>
            <div className="overline">Block time</div>
            <h4 className="font-heading text-lg uppercase">Off-hours / vacations</h4>
          </div>
        </div>
        <form onSubmit={addBlock} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <input
            data-testid="block-start"
            type="datetime-local"
            required
            value={blockStart}
            onChange={(e) => setBlockStart(e.target.value)}
            className="bg-obsidian-900 border border-obsidian-600 px-2 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
          />
          <input
            data-testid="block-end"
            type="datetime-local"
            required
            value={blockEnd}
            onChange={(e) => setBlockEnd(e.target.value)}
            className="bg-obsidian-900 border border-obsidian-600 px-2 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
          />
          <input
            data-testid="block-reason"
            type="text"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Reason (optional)"
            className="bg-obsidian-900 border border-obsidian-600 px-2 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
          />
          <button
            type="submit"
            data-testid="block-submit"
            disabled={saving}
            className="blaze-btn px-3 py-2 text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Ban className="h-3 w-3" /> Block
          </button>
        </form>

        {blocks.length > 0 && (
          <div className="space-y-1">
            <div className="overline text-[9px] text-zinc-500">/ Blocked</div>
            {blocks.map((b) => (
              <div
                key={b.id}
                data-testid={`block-row-${b.id}`}
                className="flex items-center gap-3 text-xs text-zinc-300 py-1 border-b border-obsidian-600/60 last:border-b-0"
              >
                <ClipboardList className="h-3 w-3 text-zinc-500" />
                <span className="flex-1 truncate">
                  {fmt(b.start_at)} → {fmt(b.end_at)}
                  {b.reason && <span className="text-zinc-500"> · {b.reason}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => removeBlock(b.id)}
                  data-testid={`block-delete-${b.id}`}
                  className="text-zinc-500 hover:text-blaze"
                  aria-label="Un-block"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
